import React, { cloneElement } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import DragHandle from './DragHandle';
import getBrowserDimensions from '../utils/getBrowserDimensions';
import { resolve } from '@cvent/nucleus-dynamic-css';
import removeKeys from '../utils/removeKeys';
import { resolveTestId } from '@cvent/nucleus-test-automation';
/**
* Removes any DragContainer specific properties from a props object that would be invalid
* if applied to a base DOM node. Useful when composing multiple components to prevent
* passing a component props that it doesn't use.
*/
export function removeNonStandardProps(props) {
  return removeKeys(props, ['children', 'allowDragging', 'centerOnMount', 'initialLeft', 'initialTop',
    'maxLeft', 'maxTop', 'minLeft', 'minTop', 'onDragHandler', 'classes']);
}

/**
DragContainer - A wrapper component to make the child content draggable. Must have a direct DragHandle
child component to accept the dragStart function. The container will always be forced to have an
absolute position style, as well as internally computed top and left styles.
**/
export default class DragContainer extends React.Component {
  static displayName = 'DragContainer';
  static propTypes = {
    /** The child content for the container. */
    children: PropTypes.node.isRequired,
    /** Flag indicating whether dragging is turned on. */
    allowDragging: PropTypes.bool,
    /** Flag indicating whether to center the container on the page when mounted. */
    centerOnMount: PropTypes.bool,
    /** An initial left value for the container if centerOnMount is false. */
    initialLeft: PropTypes.number,
    /** An inital top value for the container if centerOnMount is false. */
    initialTop: PropTypes.number,
    /** Maximum left value for the dragging **/
    maxLeft: PropTypes.number,
    /** Maximum top value for the dragging **/
    maxTop: PropTypes.number,
    /** Minimum left value for dragging, defaults 0 **/
    minLeft: PropTypes.number,
    /** Minimum top value for dragging, defaults 0 **/
    minTop: PropTypes.number,
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Top level style applied to the container. */
      dragContainer: PropTypes.object
    }),
    /** Called with new left and top position as it changes **/
    onDragHandler: PropTypes.func
  };
  static defaultProps = {
    allowDragging: true,
    centerOnMount: false,
    initialLeft: 0,
    initialTop: 0,
    maxLeft: Number.MAX_VALUE,
    maxTop: Number.MAX_VALUE,
    minLeft: 0,
    minTop: 0
  };

  constructor(props) {
    super(props);
    this.state = {
      left: props.initialLeft,
      top: props.initialTop
    };
    this.dragFunction = this.dragFunction.bind(this);
    this.endDragFunction = this.endDragFunction.bind(this);
    this.dragStart = this.dragStart.bind(this);
  }

  componentDidMount() {
    if (this.props.centerOnMount) {
      // Center the container.
      // We can't do this until after the container is mounted because we don't know how big it is until then.
      const container = ReactDOM.findDOMNode(this.refs.draggable);
      const browserMeasures = getBrowserDimensions(window, document);
      const containerWidth = Math.max(container.clientWidth, container.offsetWidth);
      const containerHeight = Math.max(container.clientHeight, container.offsetHeight);
      const positionTop = browserMeasures.amountScrolledY + (browserMeasures.browserHeight / 2) - (containerHeight / 2);
      container.style.top = positionTop > 0 ? positionTop + 'px' : 0;
      container.style.left = browserMeasures.amountScrolledX +
                            (browserMeasures.browserWidth / 2) - (containerWidth / 2) + 'px';

      /* eslint-disable react/no-did-mount-set-state */
      // This doesn't fire render again, but keeps the state in sync.
      this.setState({
        left: container.style.left,
        top: container.style.top
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    const { initialLeft: newLeft, initialTop: newTop } = nextProps;
    const { initialLeft, initialTop } = this.props;
    if ((initialLeft !== newLeft) || (initialTop !== newTop)) {
      this.setState({
        left: newLeft,
        top: newTop
      });
    }
  }

  dragFunction(e) {
    // Re-render the container with the new left and top values.
    const { maxLeft, maxTop, minLeft, minTop } = this.props;
    const newLeft = Math.min(maxLeft, Math.max(minLeft, e.clientX - this.state.differenceX));
    const newTop = Math.min(maxTop, Math.max(minTop, e.clientY - this.state.differenceY));
    this.setState({
      left: newLeft,
      top: newTop
    });
    if (this.props.onDragHandler) {
      this.props.onDragHandler(newLeft, newTop);
    }
  }

  endDragFunction() {
    // Remove the drag functions.
    if (document && document.removeEventListener) {
      document.removeEventListener('mousemove', this.dragFunction, false);
      document.removeEventListener('mouseup', this.endDragFunction, false);
      document.removeEventListener('touchend', this.endDragFunction, false);
    }
  }

  dragStart(e) {
    // Stop item from Dragging if prop is set to false
    if (!this.props.allowDragging) {
      return;
    }
    // Save the difference in x and y positions to be used when dragging the container around.
    const container = ReactDOM.findDOMNode(this.refs.draggable);
    this.setState({
      differenceX: e.clientX - container.offsetLeft,
      differenceY: e.clientY - container.offsetTop
    });
    // Attach the drag and end drag events.
    if (document && document.addEventListener) {
      document.addEventListener('mousemove', this.dragFunction, false);
      document.addEventListener('mouseup', this.endDragFunction, false);
      document.addEventListener('touchend', this.endDragFunction, false);
    }

    // Prevent default so that the mouse click doesnt start selecting text or elements on
    // the page while you are dragging.
    if (e.preventDefault) {
      e.preventDefault();
    }
  }

  startDragAtNewPostion(left, top, clientX, clientY) {
    this.setState({
      left,
      top
    }, () => {
      this.dragStart({ clientX, clientY });
    });
  }

  render() {
    // Pass dragStart function to any DragHandle children.
    let dragHandleExists = false;
    const children = React.Children.map(this.props.children, (child) => {
      if (child && child.type === DragHandle) {
        dragHandleExists = true;
        return cloneElement(child, { dragStart: this.dragStart });
      }
      return child;
    });

    // If no drag handle exists in the children, wrap the entire content in a drag handle.
    const content = dragHandleExists ? children : (
      <DragHandle dragStart={this.dragStart}>{children}</DragHandle>
    );

    const styleObject = resolve(this.props, 'dragContainer');
    styleObject.style.position = 'absolute';
    styleObject.style.left = this.state.left;
    styleObject.style.top = this.state.top;
    return (
      <div
        {...resolveTestId(this.props)}
        {...removeNonStandardProps(this.props)}
        {...styleObject} ref="draggable">
        {content}
      </div>
    );
  }
}
